import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "May 11, 2026";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
        { name: "description", content: "How Colorado Web Impressions uses cookies, analytics, and tracking technologies on coloradowebimpressions.com, plus your choices and opt-outs." },
      { title: "Cookie Policy | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "What cookies coloradowebimpressions.com uses, why, and how to disable them. Plain-English cookie policy.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Cookie Policy | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/cookies` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Cookie Policy", path: "/cookies" },
      ]),
    ],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="Cookie Policy"
        description={`Last updated: ${LAST_UPDATED}. The short, honest version of which cookies we set and why.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <p>
              This cookie policy is issued by Heidlebaugh Digital LLC (dba
              Colorado Web Impressions), the operator of coloradowebimpressions.com.
            </p>

            <h2>What cookies are</h2>
            <p>
              Cookies are small text files a website stores in your browser.
              They let the site remember things like whether you&apos;ve
              dismissed a popup, and let us see aggregated, anonymized usage
              data so we can improve the site.
            </p>

            <h2>Cookies we use</h2>
            <ul>
              <li>
                <strong>Strictly necessary (first-party).</strong> Used for
                basic site functionality such as remembering that you closed our
                exit-intent popup, or storing your session if you log in to an
                admin area. The site does not work properly without these.
              </li>
              <li>
                <strong>Analytics (first-party, anonymized).</strong> Aggregated
                page views, device type, and referring source. We do not link
                this data to a specific person.
              </li>
              <li>
                <strong>Advertising / retargeting cookies.</strong> We do not
                currently run third-party retargeting pixels (Google Ads, Meta
                Pixel, LinkedIn Insight, etc.) on this site. If that ever
                changes, we will update this page first and add a consent
                banner.
              </li>
            </ul>

            <h2>How to disable cookies</h2>
            <p>
              Every major browser lets you block or delete cookies. Disabling
              strictly necessary cookies may break parts of the site.
            </p>
            <ul>
              <li>
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  rel="nofollow noreferrer"
                  target="_blank"
                  className="font-bold text-brand-gold-deep underline"
                >
                  Chrome
                </a>
              </li>
              <li>
                <a
                  href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                  rel="nofollow noreferrer"
                  target="_blank"
                  className="font-bold text-brand-gold-deep underline"
                >
                  Firefox
                </a>
              </li>
              <li>
                <a
                  href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                  rel="nofollow noreferrer"
                  target="_blank"
                  className="font-bold text-brand-gold-deep underline"
                >
                  Safari
                </a>
              </li>
              <li>
                <a
                  href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  rel="nofollow noreferrer"
                  target="_blank"
                  className="font-bold text-brand-gold-deep underline"
                >
                  Edge
                </a>
              </li>
            </ul>

            <h2>More on privacy</h2>
            <p>
              See our{" "}
              <Link to="/privacy" className="font-bold text-brand-gold-deep underline">
                how we handle your data
              </Link>{" "}
              for how we handle the rest of your information, and{" "}
              <Link to="/do-not-sell" className="font-bold text-brand-gold-deep underline">
                Your Privacy Choices
              </Link>{" "}
              for California residents.
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
